import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminDeptApi from "../../api/admin/AdminDept";
import {
  AddDeptParam,
  ChangeDeptNameParam,
  Dept,
  DeptDetail,
  DeptInfo,
  DeptManagerParam,
  ExternalUser,
  TreeNode,
  UpdateDeptParam,
  User,
  UserDetail,
  UserDTO,
  UserPasswd,
} from "../../../types/adminDept";
import dayjs from "dayjs";
import FileSaver from "file-saver";

const ROOT_DEPT_CODE = "ae4fc0f5fd8f4142834c1ad4e929a514";
// # initial state
const initialState = {
  deptDetail: {} as DeptDetail,
  userDetail: {} as UserDetail,
  deptList: [] as Dept[],
  internalTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  internalDeptTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  internalActiveTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  internalSearchTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalActiveTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalSearchTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
};

export const trunkGetDeptLists = createAsyncThunk(
  "wapi/admins/depts",
  async () => {
    const resp = await adminDeptApi.getDeptLists();
    return resp;
  }
);

export const trunkGetDeptDetail = createAsyncThunk(
  "wapi/admins/depts/deptId/detail",
  async (deptId: string) => {
    const resp = await adminDeptApi.getDeptDetail(deptId);
    return { resp, deptId };
  }
);

export const trunkGetDeptUsers = createAsyncThunk(
  "wapi/admins/depts/deptId/users",
  async (deptId: string) => {
    const resp = await adminDeptApi.getDeptUsers(deptId);
    return { resp, deptId };
  }
);

export const trunkGetDeptInfo = createAsyncThunk(
  "wapi/admins/depts/info",
  async (deptId: string) => {
    const resp = await adminDeptApi.getDeptInfo(deptId);
    return resp;
  }
);

export const trunkGetUserDetail = createAsyncThunk(
  "wapi/admins/users/info",
  async (userId: string) => {
    const resp = await adminDeptApi.getUserDetail(userId);
    return resp;
  }
);

export const trunkUpdateUserDept = createAsyncThunk(
  "wapi/admins/users/update",
  async (userInfo: UserDTO) => {
    const resp = await adminDeptApi.updateUserDept(userInfo);
    return resp;
  }
);

export const trunkUpdateExternalUserDept = createAsyncThunk(
  "wapi/admins/users/update/external",
  async (userInfo: ExternalUser) => {
    const resp = await adminDeptApi.updateExternalUserDept(userInfo);
    return resp;
  }
);

export const trunkAddUserDept = createAsyncThunk(
  "wapi/admins/users/insert",
  async (userInfo: UserDTO) => {
    const resp = await adminDeptApi.addUserDept(userInfo);
    return resp;
  }
);

export const trunkGetActiveInternalUserList = createAsyncThunk(
  "wapi/admins/users/list/internal/active",
  async () => {
    const resp = await adminDeptApi.getActiveInternalUserList();
    return resp;
  }
);

export const trunkGetActiveExternalUserList = createAsyncThunk(
  "wapi/admins/users/list/external/active",
  async () => {
    const resp = await adminDeptApi.getActiveExternalUserList();
    return resp;
  }
);

export const trunkGetInternalUserList = createAsyncThunk(
  "wapi/admins/users/list/internal",
  async (searchOpt: string) => {
    const resp = await adminDeptApi.getInternalUserList(searchOpt);
    return resp;
  }
);

export const trunkGetExternalUserList = createAsyncThunk(
  "wapi/admins/users/list/external",
  async (searchOpt: string) => {
    const resp = await adminDeptApi.getExternalUserList(searchOpt);
    return resp;
  }
);

export const trunkDownloadDeptFile = createAsyncThunk(
  "wapi/admins/users/list/excel",
  async () => {
    const resp = await adminDeptApi.downloadDeptFile();
    return resp;
  }
);

export const trunkUploadDeptFile = createAsyncThunk(
  "wapi/admins/users/list/excel/upload",
  async (files: any) => {
    const resp = await adminDeptApi.uploadDeptFile(files);
    return resp;
  }
);

export const trunkUpdateDeptParent = createAsyncThunk(
  "wapi/admins/depts/update",
  async ({ code, parentCode }: UpdateDeptParam) => {
    const resp = await adminDeptApi.updateDeptParent(code, parentCode);
    return resp;
  }
);

export const trunkUpdateDeptManager = createAsyncThunk(
  "wapi/admins/depts/update/manager",
  async (param: DeptManagerParam) => {
    const resp = await adminDeptApi.updateDeptManager(param);
    return resp;
  }
);

export const trunkDeleteDept = createAsyncThunk(
  "wapi/admins/depts/delete/dept",
  async (code: string) => {
    const resp = await adminDeptApi.deleteDept(code);
    return resp;
  }
);

export const trunkDeleteUser = createAsyncThunk(
  "wapi/admins/depts/delete/user",
  async (list: { id: string }[]) => {
    const resp = await adminDeptApi.deleteUser(list);
    return resp;
  }
);

export const trunkAddDept = createAsyncThunk(
  "wapi/admins/depts/insert",
  async (param: AddDeptParam) => {
    const resp = await adminDeptApi.addDept(param);
    return resp;
  }
);

export const trunkUserPasswdUpdate = createAsyncThunk(
  "wapi/admins/users/passwd/update",
  async (param: UserPasswd) => {
    const resp = await adminDeptApi.userPasswdUpdate(param);
    return resp;
  }
);

export const trunkUserPasswdInit = createAsyncThunk(
  "papi/passwd/init/admin",
  async (param: string) => {
    const resp = await adminDeptApi.userPasswdInit(param);
    return resp;
  }
);

export const trunkChangeDeptName = createAsyncThunk(
  "wapi/admins/depts/update/name",
  async (param: ChangeDeptNameParam) => {
    const resp = await adminDeptApi.changeDeptName(param);
    return resp;
  }
);

const adminDeptSlice = createSlice({
  name: "adminDept",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(trunkGetDeptLists.fulfilled, (state, action) => {
      state.deptList = action.payload;
    });
    builder.addCase(
      trunkGetDeptDetail.fulfilled,
      (state, { payload: deptDetail }) => {
        state.deptDetail.depts = deptDetail.resp;
        state.deptDetail.deptCode = deptDetail.deptId;
        if (deptDetail.deptId === ROOT_DEPT_CODE) {
          let temps: TreeNode[] = [];
          deptDetail.resp.forEach((param, idx) => {
            temps.unshift({
              columnText: param.name,
              subTree: [],
              showChildren:
                param.deptCount > 0 || param.userCount > 0 ? true : false,
              hasChildren:
                param.deptCount > 0 || param.userCount > 0 ? true : false,
              value: param.code,
              type: "dept",
            });
          });
          state.internalTree.subTree = state.internalTree.subTree.filter(
            function (data) {
              return data.type === "user";
            }
          );

          temps.forEach((param, idx) => {
            state.internalTree.subTree.unshift(param);
          });
        } else {
          state.internalTree.subTree.forEach((param) => {
            makeDeptSubNodes(deptDetail.resp, "dept", deptDetail.deptId, param);
          });
        }
      }
    );
    builder.addCase(
      trunkGetDeptUsers.fulfilled,
      (state, { payload: userDetail }) => {
        state.userDetail.users = userDetail.resp;
        state.userDetail.deptCode = userDetail.deptId;
        if (userDetail.deptId === ROOT_DEPT_CODE) {
          state.internalTree.subTree = state.internalTree.subTree.filter(
            function (data) {
              return data.type === "dept";
            }
          );
          userDetail.resp.forEach((param) => {
            state.internalTree.subTree.push({
              columnText: param.name,
              subTree: [],
              showChildren: false,
              hasChildren: false,
              value: param.id,
              type: "user",
            });
          });
          //  state.internalTree.setUser = true;
        } else {
          state.internalTree.subTree.forEach((param) => {
            makeUserSubNodes(userDetail.resp, "dept", userDetail.deptId, param);
          });
        }
      }
    );
    builder.addCase(trunkGetExternalUserList.fulfilled, (state, action) => {
      if (action.meta.arg !== "") {
        state.externalSearchTree.subTree = [];
        action.payload.forEach((param) => {
          state.externalSearchTree.subTree.push({
            columnText: "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      } else {
        state.externalTree.subTree = [];
        action.payload.forEach((param) => {
          state.externalTree.subTree.push({
            columnText: "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      }
    });
    builder.addCase(
      trunkGetInternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.internalSearchTree.subTree = [];
        userList.users.forEach((param) => {
          state.internalSearchTree.subTree.push({
            columnText: param.name,
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.id,
            type: "user",
          });
        });
      }
    );
    builder.addCase(
      trunkGetActiveInternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.internalActiveTree.subTree = [];
        userList.forEach((param) => {
          state.internalActiveTree.subTree.push({
            columnText: param.userName,
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "user",
          });
        });
      }
    );
    builder.addCase(
      trunkGetActiveExternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.externalActiveTree.subTree = [];
        userList.forEach((param) => {
          state.externalActiveTree.subTree.push({
            columnText: param.userName,
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      }
    );
    builder.addCase(
      trunkChangeDeptName.fulfilled,
      (state, { payload: result }) => {
        //조직도(트리) 쪽 부서명 변경
        state.internalTree.subTree.forEach((param) => {
          if (param.value === result.code) {
            param.columnText = result.name;
          } else {
            updateTreeItem(
              param.subTree,
              "",
              result.code,
              result.name,
              state.internalTree,
              "dept"
            );
          }
        });
        state.userDetail.users.forEach((item) => {
          if (item.dept.code === result.code) {
            item.dept.name = result.name;
          }
        });
      }
    );
    builder.addCase(trunkAddDept.fulfilled, (state, { payload: result }) => {
      const parentCode = result.parentCode;
      const code = result.code;

      //부서 삽입(하위부서 테이블)
      state.deptDetail.depts.push({
        code: result.code,
        name: result.name,
        managerId: "",
        managerName: "",
        deptCount: 0,
        userCount: 0,
      });

      // 정렬
      state.deptDetail.depts.sort(function (a: DeptInfo, b: DeptInfo) {
        const upperCaseA = a.name.toUpperCase();
        const upperCaseB = b.name.toUpperCase();

        if (upperCaseA > upperCaseB) return 1;
        if (upperCaseA < upperCaseB) return -1;
        if (upperCaseA === upperCaseB) return 0;
        return 0;
      });

      //부서 삽입(트리)
      if (ROOT_DEPT_CODE === parentCode) {
        state.internalTree.subTree.push({
          columnText: result.name,
          subTree: [],
          showChildren: false,
          hasChildren: false,
          value: code,
          type: "dept",
        });
        state.internalTree.subTree.sort(function (a: TreeNode, b: TreeNode) {
          return sortFunction(
            a.columnText + "",
            b.columnText + "",
            a.type,
            b.type
          );
        });
      } else {
        state.internalTree.subTree.forEach((param, index) => {
          if (param.value === parentCode) {
            param.subTree.push({
              columnText: result.name,
              subTree: [],
              showChildren: false,
              hasChildren: false,
              value: code,
              type: "dept",
            });
            param.hasChildren = true;

            param.subTree.sort(function (a: TreeNode, b: TreeNode) {
              return sortFunction(
                a.columnText + "",
                b.columnText + "",
                a.type,
                b.type
              );
            });
          } else if (param.subTree.length > 0 && parentCode) {
            updateTreeItem(
              param.subTree,
              parentCode,
              code,
              result.name,
              state.internalTree,
              "dept"
            );
          }
        });
      }
    });
    builder.addCase(trunkUpdateDeptParent.fulfilled, (state, action) => {
      const dept = action.payload;
      const code = dept.code;
      const parentCode = dept.parentCode;
      let showChildren = false;
      let hasChildren = false;
      //이동 대상 부서 삭제
      state.internalTree.subTree.forEach((param, index) => {
        if (param.value === code) {
          showChildren = param.showChildren;
          hasChildren = param.hasChildren;
          state.internalTree.subTree.splice(index, 1);
        } else if (param.subTree.length > 0) {
          hasChildren = removeTreeItem(
            param,
            code,
            state.internalTree,
            "remove"
          );
        }
      });
      //이동 대상 부서 삽입
      // ROOT 체크
      if (parentCode === ROOT_DEPT_CODE) {
        state.internalTree.subTree.push({
          columnText: dept.name,
          subTree: [],
          showChildren: showChildren,
          hasChildren: hasChildren,
          value: code,
          type: "dept",
        });
        state.internalTree.subTree.sort(function (a: TreeNode, b: TreeNode) {
          return sortFunction(a.columnText, b.columnText, a.type, b.type);
        });
      } else {
        state.internalTree.subTree.forEach((param, index) => {
          if (param.value === parentCode) {
            param.subTree.push({
              columnText: dept.name,
              subTree: [],
              showChildren: showChildren,
              hasChildren: hasChildren,
              value: code,
              type: "dept",
            });

            param.subTree.sort(function (a: TreeNode, b: TreeNode) {
              return sortFunction(a.columnText, b.columnText, a.type, b.type);
            });
          } else if (param.subTree.length > 0 && parentCode) {
            updateTreeItem(
              param.subTree,
              parentCode,
              code,
              dept.name,
              state.internalTree,
              "dept",
              showChildren,
              hasChildren
            );
          }
        });
      }
    });
    builder.addCase(
      trunkDownloadDeptFile.fulfilled,
      (state, { payload: res }) => {
        if (res.data && res.headers) {
          const file = res.data;
          const fileName = dayjs().format("YYYYMMDD") + "_users.xlsx";
          FileSaver.saveAs(file, fileName);
        }
      }
    );
  },
});

const makeDeptSubNodes = (
  items: DeptInfo[],
  clickType: string,
  clickValue: string,
  treeNode: TreeNode
): void => {
  if (treeNode.type === clickType && treeNode.value === clickValue) {
    let temps: TreeNode[] = [];
    items.forEach((param, idx) => {
      temps.unshift({
        columnText: param.name,
        subTree: [],
        showChildren: param.deptCount > 0 || param.userCount > 0 ? true : false,
        hasChildren: param.deptCount > 0 || param.userCount > 0 ? true : false,
        value: param.code,
        type: "dept",
      });
    });
    //  treeNode.setDept = true;
    treeNode.showChildren = true;
    // treeNode.hasChildren = true;

    treeNode.subTree = treeNode.subTree.filter(function (data) {
      return data.type === "user";
    });

    temps.forEach((param, idx) => {
      treeNode.subTree.unshift(param);
    });
  } else {
    treeNode.subTree.forEach((param) => {
      makeDeptSubNodes(items, clickType, clickValue, param);
    });
  }
};

const makeUserSubNodes = (
  items: User[],
  clickType: string,
  clickValue: string,
  treeNode: TreeNode
): void => {
  if (treeNode.type === clickType && treeNode.value === clickValue) {
    treeNode.subTree = treeNode.subTree.filter(function (data) {
      return data.type === "dept";
    });

    items.forEach((param) => {
      treeNode.subTree.push({
        columnText: param.name,
        subTree: [],
        showChildren: false,
        hasChildren: false,
        value: param.id,
        type: "user",
      });
    });
    // treeNode.setUser = true;
    treeNode.showChildren = true;
    //  treeNode.hasChildren = true;
  } else {
    treeNode.subTree.forEach((param) => {
      makeUserSubNodes(items, clickType, clickValue, param);
    });
  }
};

const updateTreeItem = (
  tree: TreeNode[],
  parentCode: string,
  code: string,
  name: string,
  state: TreeNode,
  type: string,
  showChildren?: boolean,
  hasChildren?: boolean
) => {
  tree.forEach((param, index) => {
    if (type === "dept" && param.value === parentCode) {
      //부서 이동일때
      param.subTree.push({
        columnText: name,
        subTree: [],
        showChildren: showChildren ? showChildren : false,
        hasChildren: hasChildren ? hasChildren : false,
        value: code,
        type: "dept",
      });
      param.hasChildren = true;

      param.subTree.sort(function (a: TreeNode, b: TreeNode) {
        return sortFunction(
          a.columnText + "",
          b.columnText + "",
          a.type,
          b.type
        );
      });
    } else if (type === "user" && param.value === parentCode) {
      //사용자 이동일때
      param.subTree.push({
        columnText: name,
        subTree: [],
        showChildren: false,
        hasChildren: false,
        value: code,
        type: "user",
      });
      param.hasChildren = true;

      param.subTree.sort(function (a: TreeNode, b: TreeNode) {
        return sortFunction(
          a.columnText + "",
          b.columnText + "",
          a.type,
          b.type
        );
      });
    } else if (
      (type === "dept" || type === "user") &&
      parentCode === "" &&
      param.subTree.length === 0
    ) {
      //부서명만 업데이트 시
      tree.forEach((item) => {
        if (item.value === code) {
          item.columnText = name;
        }
      });
    } else if (param.subTree.length > 0) {
      updateTreeItem(
        param.subTree,
        parentCode,
        code,
        name,
        state,
        type,
        showChildren,
        hasChildren
      );
    }
  });
};

const removeTreeItem = (
  subTree: TreeNode,
  deptCode: string,
  deptTree: TreeNode,
  status: string
): boolean => {
  let hasChildren = false;
  subTree.subTree.forEach((param, index) => {
    let isFind = false;
    if (param.value === deptCode) {
      hasChildren = param.hasChildren;
      subTree.subTree.splice(index, 1);
      if (status === "remove") {
        isFind = true;
      }
    } else if (param.subTree.length > 0) {
      return removeTreeItem(param, deptCode, deptTree, status);
    }

    if (isFind && subTree.subTree.length === 0) {
      subTree.hasChildren = false;
    }
  });

  return hasChildren;
};

const sortFunction = (
  a_columnText: string,
  b_columnText: string,
  a_type: string,
  b_type: string
): number => {
  const upperCaseA = a_columnText.toUpperCase();
  const upperCaseB = b_columnText.toUpperCase();
  if (a_type === "dept" && b_type === "user") return -1;
  if (a_type === "user" && b_type === "dept") return 1;
  if (upperCaseA > upperCaseB) return 1;
  if (upperCaseA < upperCaseB) return -1;
  if (upperCaseA === upperCaseB) return 0;
  return 0;
};

export const adminDeptActions = adminDeptSlice.actions;
export default adminDeptSlice.reducer;
